<template>
    <fieldset>
        <div v-if="showLabel">
            <pc-label
                v-bind:label="label"
                v-bind:show-required="required"
                v-bind:show-help="showHelp"
                v-bind:help-link="showHelpLinkOnly"
                v-on:help-clicked="$emit('help-clicked')" />
            <div>
                <slot name="help" />
            </div>
        </div>
        <div class="row gx-3">
            <pc-loader
                v-bind:loading="retrievingSasToken"
                v-bind:class="responsiveClasses.mapContainer"
                class="d-flex flex-column position-relative">
                <pc-alert
                    v-if="showLocationMap"
                    v-bind:shown="showInvitationToEdit"
                    class="mb-1">
                    {{ invitationToEditMessage }}
                </pc-alert>
                <pc-alert v-bind="dependencyAlertBinder">
                    <div>
                        <span>
                            {{ dependencyChangedAlertText }}
                        </span>
                        <pc-button
                            link
                            v-on:click="$emit('geocode-from-dependency', $event)">
                            {{ dependencyChangedButtonLabel }}
                        </pc-button>
                    </div>
                </pc-alert>
                <pc-map
                    v-if="usingLocationMap"
                    ref="MapControl"
                    v-bind:config="mapConfig"
                    v-bind:class="responsiveClasses.mapControl"
                    v-bind:allow-service-retry="!readonly"
                    class="map-container w-100"
                    manual-initialize
                    data-test-role="locationMap"
                    v-on:initialize-failed="$emit('map-initialize-failed', $event)"
                    v-on:retry-init-button-clicked="$emit('reinitialize-map', $event)" />
                <div
                    v-if="showDependencyOverlay && usingLocationMap"
                    class="mapwait-overlay d-flex justify-content-center align-items-center">
                    <div class="d-flex flex-column justify-content-center align-items-center rounded bg-white p-4">
                        <span>{{ dependencyLoaderMessage }}</span>
                        <pc-transition>
                            <pc-button
                                outline
                                class="btn-outline-primary mt-3"
                                v-on:click="$emit('overlay-geocode-from-dependency', $event)">
                                {{ locateNowButtonLabel }}
                            </pc-button>
                        </pc-transition>
                    </div>
                </div>
            </pc-loader>
        </div>
        <div
            v-if="useDirectInput"
            class="row gx-3 mt-2">
            <pc-input-group v-bind:class="responsiveClasses.geoInput">
                <pc-input
                    v-bind="latitudeBinder"
                    v-bind:model-value="latitudeGetter"
                    data-test-role="latitudeInput"
                    lazy
                    v-on:update:model-value="$emit('latitude-wrapper', $event)"
                    v-on:validated="$emit('validated', 'manualLat', $event)" />
            </pc-input-group>
            <pc-input-group v-bind:class="responsiveClasses.geoInput">
                <pc-input
                    v-bind="longitudeBinder"
                    v-bind:model-value="longitudeGetter"
                    data-test-role="longitudeInput"
                    lazy
                    v-on:update:model-value="$emit('longitude-wrapper', $event)"
                    v-on:validated="$emit('validated', 'manualLong', $event)" />
            </pc-input-group>
        </div>
        <div
            v-else-if="editingOverrideManually"
            class="row gx-3 mt-2">
            <pc-input-group v-bind:class="responsiveClasses.geoInput">
                <pc-input
                    v-bind="latitudeBinder"
                    v-bind:validation-group-id="groupId"
                    v-bind:model-value="overrideLatitudeGetter"
                    data-test-role="overrideLatitudeInput"
                    v-on:update:model-value="$emit('override-latitude-wrapper', $event)"
                    v-on:validated="$emit('validated', 'manualLat', $event)" />
            </pc-input-group>
            <pc-input-group v-bind:class="responsiveClasses.geoInput">
                <pc-input
                    v-bind="longitudeBinder"
                    v-bind:validation-group-id="groupId"
                    v-bind:model-value="overrideLongitudeGetter"
                    data-test-role="overrideLongitudeInput"
                    v-on:update:model-value="$emit('override-longitude-wrapper', $event)"
                    v-on:validated="$emit('validated', 'manualLong', $event)" />
            </pc-input-group>
            <pc-input-group class="d-flex align-items-center ms-3">
                <pc-button
                    small
                    primary
                    class="ms-1"
                    data-test-role="acceptButton"
                    v-bind:disabled="disableAcceptButton"
                    v-on:click="$emit('accept-manual-override', $event)">
                    {{ updateButtonLabel }}
                </pc-button>
                <pc-button
                    small
                    class="ms-1"
                    data-test-role="cancelButton"
                    v-on:click="$emit('cancel-manual-override', $event)">
                    {{ cancelButtonLabel }}
                </pc-button>
            </pc-input-group>
        </div>
        <div
            v-else-if="editingOverrideViaMap"
            class="row gx-3 mt-2">
            <pc-location-formatter
                v-bind:location="overrideValue"
                v-bind:location-missing-text="noLocationSelectedText"
                class="col-auto fst-italic d-flex align-items-center" />
            <pc-input-group class="col-4 d-flex align-items-center ms-3">
                <pc-button
                    small
                    primary
                    class="ms-1"
                    data-test-role="acceptButton"
                    v-bind:disabled="disableAcceptButton"
                    v-on:click="$emit('accept-manual-override', $event)">
                    {{ updateButtonLabel }}
                </pc-button>
                <pc-button
                    small
                    class="ms-1"
                    data-test-role="cancelButton"
                    v-on:click="$emit('cancel-manual-override', $event)">
                    {{ cancelButtonLabel }}
                </pc-button>
            </pc-input-group>
        </div>
        <div
            v-else
            class="row row-cols-auto gx-3 mt-2">
            <pc-location-formatter
                v-bind:location="locationObject"
                v-bind:location-missing-text="noLocationSelectedText"
                class="fst-italic d-flex align-items-center" />
            <pc-button
                v-if="!readonly && showLocationMap"
                outline
                small
                style="min-width: 0"
                class="mx-1"
                data-test-role="openOverrideButton"
                v-bind:aria-label="manualOverrideAriaLabel"
                v-on:click="$emit('open-manual-override', $event)">
                <pc-bs-icon
                    name="pencil-fill"
                    class="text-primary" />
            </pc-button>
        </div>
        <div
            v-if="!useDirectInput"
            class="row gx-3 mt-1">
            <validation-render-helper
                v-bind:validation-error="locationValidationErrors"
                v-bind:validation-group-id="groupId" />
        </div>
    </fieldset>
</template>

<script>
    import { validationRenderHelper } from "./_elementBase";
    import pcLocationFormatter from "./location/pc-location-formatter.vue";
    import pcMap from "./location/pc-map.vue";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/location.i18n";
    import { getResponsiveClassMap } from "Responsive/manager";

    const responsiveConfig = {
        mapContainer: {
            // col-sm-10 col-lg-6
            col: { sm: 10, lg: 6 },
            always: "col"
        },
        mapControl: {
            mapheight: { xs: "standard", xl: "large" }
        },
        geoInput: {
            // col-8 col-sm-4 col-lg-2
            col: { xs: 6, sm: 4, lg: 2 }
        }
    };

    export default {
        components: { validationRenderHelper, pcLocationFormatter, pcMap },
        inject: {
            viewport: {
                from: "viewport",
                default: null
            }
        },
        provide() {
            return {
                validationHub: this.validationHub
            };
        },
        props: {
            label: { type: String, required: false, default: null },
            showLabel: { type: Boolean, required: false, default: true },
            required: { type: Boolean, required: true },
            showHelp: { type: Boolean, required: true },
            showHelpLinkOnly: { type: String, required: false, default: null },
            readonly: { type: Boolean, required: true },
            isInFormEditor: { type: Boolean, required: true },
            locationObject: { type: Object, required: true },
            longitudeGetter: { type: String, required: true },
            latitudeGetter: { type: String, required: true },
            overrideValue: { type: Object, required: false, default: null },
            retrievingSasToken: { type: Boolean, required: true },
            mapConfig: { type: Object, required: false, default: null },
            useDirectInput: { type: Boolean, required: true },
            editingOverrideManually: { type: Boolean, required: true },
            editingOverrideViaMap: { type: Boolean, required: true },
            overrideLongitudeGetter: { type: String, required: true },
            overrideLatitudeGetter: { type: String, required: true },
            longitudeBinder: { type: Object, required: true },
            latitudeBinder: { type: Object, required: true },
            locationValidationErrors: { type: Array, required: false, default: null },
            locationChanged: { type: Boolean, required: true },
            showInvitationToEdit: { type: Boolean, required: true },
            disableAcceptButton: { type: Boolean, required: true },
            showDependencyOverlay: { type: Boolean, required: true },
            showLocationMap: { type: Boolean, required: true },
            usingLocationMap: { type: Boolean, required: true },
            dependencyAlertBinder: { type: Object, required: true },
            dependencyLoaderMessage: { type: String, required: true },
            invitationToEditMessage: { type: String, required: true },
            validationHub: { type: Object, required: true },
            element: { type: Object, required: true }
        },
        emits: [
            "help-clicked",
            "update",
            "validated",
            "map-initialize-failed",
            "reinitialize-map",
            "cancel-manual-override",
            "accept-manual-override",
            "open-manual-override",
            "latitude-wrapper",
            "longitude-wrapper",
            "override-latitude-wrapper",
            "override-longitude-wrapper",
            "overlay-geocode-from-dependency",
            "geocode-from-dependency",
            "update-has-location-validation-errors"
        ],
        computed: {
            ...mapI18n(
                {
                    ...KEYS
                },
                NAMESPACE
            ),
            responsiveClasses() {
                return getResponsiveClassMap(this.viewport, responsiveConfig);
            },
            groupId() {
                return this.element.PublicId + "Location";
            },
            hasLocationValidationErrors() {
                const hasErrors = this.locationValidationErrors && this.locationValidationErrors.length > 0;
                return hasErrors;
            }
        },
        watch: {
            hasLocationValidationErrors() {
                this.$emit("update-has-location-validation-errors", this.hasLocationValidationErrors);
            }
        },
        methods: {
            initializeMapControl(newMapConfig) {
                if (this.$refs && this.$refs.MapControl) {
                    this.$refs.MapControl.initialize(newMapConfig);
                }
            },
            reinitializeMapControl() {
                if (this.$refs && this.$refs.MapControl) {
                    this.$refs.MapControl.reinitialize();
                }
            },
            reSizeMapControl() {
                if (this.$refs && this.$refs.MapControl) {
                    this.$refs.MapControl.resizeMap();
                }
            }
        }
    };
</script>

<style lang="scss" scoped>
    .mapheight-standard {
        height: 300px;
    }

    .mapheight-large {
        height: 400px;
    }

    .map-container {
        /// Used to style the markers in pcMapConfig.js
        :deep(.marker-container) {
            height: 1em;
            width: 1em;
            font-size: 3.5rem;
            cursor: pointer;

            .data-marker {
                color: var(--bs-primary);
            }
            .select-marker {
                color: var(--pc-accent-one);
            }

            .marker-pulse {
                border-radius: 50%;
                height: 0.8em;
                width: 1em;
                position: absolute;
                left: 50%;
                top: 50%;
                margin: 0 0 0 -0.5em;
                z-index: -1;
                animation: pulsate 1s ease-out;
                animation-iteration-count: infinite;
                box-shadow: 0 0 1px 2px var(--pc-accent-one-300);
                opacity: 0;
                animation-delay: 0.5s;
            }

            @keyframes pulsate {
                0% {
                    transform: scale(0.1, 0.1);
                    opacity: 0;
                }

                50% {
                    opacity: 1;
                }

                100% {
                    transform: scale(1.1, 1.1);
                    opacity: 0;
                }
            }
        }
    }

    .mapwait-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 999;
        margin: 0;
        padding: 0;

        ::before {
            background-color: black;
            opacity: 0.25;
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            content: "";
            z-index: -1;
        }
    }
</style>
